import React, { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Alert, MenuItem, Paper, Snackbar, Stack } from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';

import { ViewModeToggle } from './ViewModeToggle';
import { GENERATE_LABELS_MUTATION } from '../../../gql';
import { FormattedMessage } from 'react-intl';
import { ContentSafetyActions } from '../../../components/ContentSafetyActions';

const fullWidthStyles = { width: '100%' };
const actionPaperStyles = { bgcolor: 'transparent' };

interface SiteActionBarProps {
  findListMode: 'grid' | 'gallery';
  setFindListMode: React.Dispatch<React.SetStateAction<'grid' | 'gallery'>>;
  site?: {
    createdBy?: string;
    shared?: boolean;
  };
}

const SiteActionBar = ({
  findListMode,
  setFindListMode,
  site,
}: SiteActionBarProps) => {
  const location = useLocation();
  const { siteId } = useParams();
  const [downloadSnackOpen, setDownloadSnackOpen] = useState(false);
  const isFinds = location.pathname.endsWith('/finds');

  const [generateLabels, { loading: loadingGenerate }] = useMutation(
    GENERATE_LABELS_MUTATION,
    {
      onCompleted: async ({ generateLabels: { __typename, url } }) => {
        setDownloadSnackOpen(false);
        // start download...
        if (!!url) {
          const parts = url.split('/');
          const buffer = await fetch(url, {
            mode: 'cors',
          }).then((response) => response.arrayBuffer());
          const data = Buffer.from(buffer).toString('base64');
          const fileURL = `data:application/pdf;base64,${data}`;

          let alink = document.createElement('a');
          alink.href = fileURL;
          alink.download = parts[parts.length - 1];
          alink.click();
        }
      },
    }
  );

  const canGenerateLabels = Boolean(site) && !loadingGenerate;

  const handleGenerateLabelsClick = useCallback(() => {
    generateLabels({ variables: { id: siteId } });
    setDownloadSnackOpen(true);
  }, [generateLabels, siteId]);

  const menuItems = useMemo(
    () =>
      isFinds
        ? [
            <MenuItem disabled key="export">
              <FormattedMessage
                id="site.actions.exportComingSoon"
                defaultMessage="Export (coming soon)"
              />
            </MenuItem>,
            <MenuItem disabled key="report">
              <FormattedMessage
                id="site.actions.reportComingSoon"
                defaultMessage="Generate report (coming soon)"
              />
            </MenuItem>,
            <MenuItem
              disabled={!canGenerateLabels}
              key="labels"
              onClick={handleGenerateLabelsClick}
            >
              <FormattedMessage
                id="site.actions.generateLabels"
                defaultMessage="Generate labels"
              />
            </MenuItem>,
          ]
        : undefined,
    [canGenerateLabels, handleGenerateLabelsClick, isFinds]
  );

  return (
    <Paper elevation={0} sx={actionPaperStyles}>
      <Stack direction="row" gap={1} alignItems="center">
        {isFinds && (
          <ViewModeToggle
            setFindListMode={setFindListMode}
            findListMode={findListMode}
          />
        )}
        <ContentSafetyActions
          enabled={Boolean(site?.shared)}
          entityId={siteId}
          entityType="SITE"
          hasEnabledMenuItems={isFinds && canGenerateLabels}
          menuItems={menuItems}
          ownerUserId={site?.createdBy}
        />
      </Stack>
      <Snackbar open={downloadSnackOpen}>
        <Alert severity="info" sx={fullWidthStyles}>
          <FormattedMessage
            id="site.actions.downloadInProgress"
            defaultMessage="Your download is in progress…"
          />
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export { SiteActionBar };
